/**
 * ScriptBlockView — read-only renderer for a single ScriptBlock.
 * Shows pencil + X icons on hover. The block is rendered inside an absolutely
 * positioned slot (see FormattedMotionPrompt), so it sizes itself to fill its
 * container. The inner content scrolls when the slot height is smaller than
 * the text.
 *
 * Round 4:
 *  - Body uses the shared RichTextBox in read-only (`disabled`) mode so
 *    `@character` mentions render as chips (item 7).
 *  - Empty dialogue/voiceover bodies no longer render the literal `""`
 *    placeholder (item 9). The serializer still wraps the body in quotes on
 *    write, but read-mode rendering only adds quotes when there is text.
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react'
import type { ScriptBlock } from '../scriptParser'
import { TimestampEditor } from './TimestampEditor'
import { RichTextBox } from '../../shared/RichTextBox'
import type { ReactFlowCanvasNode } from '../../../../../hooks/useYCanvas'
import { createNadDebug } from '@ponpon/api-client'

const avScriptLog = createNadDebug('dw:av-script-format')

interface ScriptBlockViewProps {
  block: ScriptBlock
  hasOverlap?: boolean
  onEdit: () => void
  onDelete: () => void
  onTimestampCommit: (startSec: number, endSec: number) => void
  /** Canvas nodes for the @mention chips. */
  canvasNodes?: ReactFlowCanvasNode[]
  /** Owning VideoGenerator node id — used for NAD trace correlation. */
  nodeId?: string
}

function formatTs(start: number, end: number): string {
  const f = (n: number) => `${(Math.round(n * 10) / 10).toFixed(1)}s`
  return `${f(start)}-${f(end)}`
}

export const ScriptBlockView = memo(function ScriptBlockView({
  block,
  hasOverlap = false,
  onEdit,
  onDelete,
  onTimestampCommit,
  canvasNodes,
  nodeId,
}: ScriptBlockViewProps) {
  const [editingTs, setEditingTs] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const popoverRef = useRef<HTMLDivElement>(null)
  const deleteButtonRef = useRef<HTMLButtonElement>(null)
  const overlapClass = hasOverlap ? 'border-red-500/60' : 'border-border-subtle'
  const kindColor =
    block.kind === 'video'
      ? 'text-primary/80'
      : block.kind === 'voiceover'
        ? 'text-amber-400/80'
        : block.kind === 'sfx'
          ? 'text-purple-400/80'
          : 'text-emerald-400/80'
  const kindLabel = block.kind === 'sfx' ? 'sound effect' : block.kind
  // Item 9: only wrap dialogue/voiceover body in quotes when text is non-empty.
  const trimmedText = block.text.trim()
  const isSpeech = block.kind === 'dialogue' || block.kind === 'voiceover'
  const displayText = isSpeech && trimmedText ? `“${block.text}”` : block.text

  // Round 5 bug 3: confirm popover for block deletion.
  const dismissConfirm = useCallback(
    (dismissedBy: 'cancel' | 'escape' | 'outside-click' | 'confirm') => {
      setConfirmingDelete(false)
      if (dismissedBy !== 'confirm') {
        avScriptLog({
          event: 'block-delete-cancelled',
          nodeId,
          blockId: block.id,
          kind: block.kind,
          dismissedBy,
        })
      }
    },
    [nodeId, block.id, block.kind],
  )

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      setConfirmingDelete((prev) => {
        if (prev) return prev
        avScriptLog({
          event: 'block-delete-confirm-shown',
          nodeId,
          blockId: block.id,
          kind: block.kind,
        })
        return true
      })
    },
    [nodeId, block.id, block.kind],
  )

  const handleConfirmDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      avScriptLog({
        event: 'block-delete-confirmed',
        nodeId,
        blockId: block.id,
        kind: block.kind,
      })
      dismissConfirm('confirm')
      onDelete()
    },
    [nodeId, block.id, block.kind, dismissConfirm, onDelete],
  )

  useEffect(() => {
    if (!confirmingDelete) return
    const onDocMouseDown = (ev: MouseEvent) => {
      const target = ev.target
      if (!(target instanceof Node)) return
      if (popoverRef.current?.contains(target)) return
      if (deleteButtonRef.current?.contains(target)) return
      dismissConfirm('outside-click')
    }
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.stopPropagation()
        dismissConfirm('escape')
      }
    }
    document.addEventListener('mousedown', onDocMouseDown, true)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [confirmingDelete, dismissConfirm])

  return (
    <div
      className={`group relative bg-surface-1 rounded p-2 border ${overlapClass} h-full w-full overflow-auto`}
      data-testid={`script-block-${block.kind}-${block.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {editingTs ? (
            <TimestampEditor
              blockId={block.id}
              startSec={block.startSec}
              endSec={block.endSec}
              onCommit={(s, e) => {
                onTimestampCommit(s, e)
                setEditingTs(false)
              }}
              onCancel={() => setEditingTs(false)}
            />
          ) : (
            <span
              onDoubleClick={(e) => {
                e.stopPropagation()
                setEditingTs(true)
              }}
              onClick={(e) => e.stopPropagation()}
              className="inline-block text-xxs px-1.5 py-0.5 rounded-full bg-primary/15 text-primary/80 font-medium cursor-pointer select-none"
              data-testid={`block-ts-${block.id}`}
              title="Double-click to edit"
            >
              {formatTs(block.startSec, block.endSec)}
            </span>
          )}
          <span className={`text-xxs uppercase tracking-wider ${kindColor}`}>
            {kindLabel}
          </span>
        </div>
        <div
          className={`flex items-center gap-0.5 transition-opacity ${
            confirmingDelete ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}
        >
          <button
            type="button"
            onClick={onEdit}
            title="Edit block"
            className="w-5 h-5 rounded hover:bg-surface-2 text-text-muted hover:text-foreground flex items-center justify-center"
            data-testid={`block-edit-${block.id}`}
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            </svg>
          </button>
          <button
            ref={deleteButtonRef}
            type="button"
            onClick={handleDeleteClick}
            title="Delete block"
            className={`w-5 h-5 rounded hover:bg-destructive/20 text-text-muted hover:text-destructive flex items-center justify-center ${
              confirmingDelete ? 'bg-destructive/20 text-destructive opacity-100' : ''
            }`}
            data-testid={`block-delete-${block.id}`}
            aria-haspopup="dialog"
            aria-expanded={confirmingDelete}
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          {confirmingDelete && (
            <div
              ref={popoverRef}
              role="dialog"
              aria-label="Delete this block?"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              data-testid={`block-delete-confirm-${block.id}`}
              className="absolute right-1 top-7 z-30 rounded-md border border-border bg-surface-2 shadow-lg px-2.5 py-2 text-xs flex flex-col gap-1.5 min-w-[148px]"
            >
              <div className="text-foreground/90">Delete this block?</div>
              <div className="flex items-center justify-end gap-1.5">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    dismissConfirm('cancel')
                  }}
                  className="px-2 py-0.5 rounded text-text-muted hover:text-foreground hover:bg-surface-1"
                  data-testid={`block-delete-cancel-${block.id}`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  className="px-2 py-0.5 rounded bg-destructive/80 text-destructive-foreground hover:bg-destructive"
                  data-testid={`block-delete-confirm-btn-${block.id}`}
                >
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {block.character && (
        <p className="text-xs font-bold text-foreground/90 mt-0.5">
          {block.character}
          {block.emotion && (
            <span className="text-xxs italic text-foreground/50 ml-1">
              {block.emotion}
            </span>
          )}
        </p>
      )}
      {/* Item 7: body uses RichTextBox in read-only (disabled) mode so
          @character mentions render as chips. RichTextBox accepts
          `@[[nodeId]]` markers in `initialContent` and renders them as
          named chips via the provided `canvasNodes`. */}
      {trimmedText ? (
        <div className="mt-0.5">
          <RichTextBox
            // The key ensures the editor is fully re-hydrated when the
            // block's text changes (e.g. after Save). RichTextBox uses
            // `initialContentHydratedRef` internally and only hydrates once.
            key={`${block.id}-${block.text.length}`}
            canvasNodes={canvasNodes}
            disabled
            initialContent={displayText}
            containerClassName="w-full bg-transparent border-0 px-0 py-0 nowheel"
            editorTestId={`block-view-body-${block.id}`}
            minHeight={16}
            maxHeight={300}
            maxLength={0}
          />
        </div>
      ) : (
        <p
          className="text-xs text-foreground/40 italic leading-relaxed mt-0.5"
          data-testid={`block-view-body-${block.id}`}
        >
          {isSpeech ? '(no dialogue yet)' : '(no description yet)'}
        </p>
      )}
      {block.camera && (
        <p className="text-xxs mt-0.5">
          <span className="text-text-muted">Camera</span>{' '}
          <span className="text-foreground/70">{block.camera}</span>
        </p>
      )}
    </div>
  )
})
